// src/modules/gyms/components/ServiceList.tsx
import { useMemo, useState, useEffect } from "react";
import { GymBranch, GymService } from "../models/gym.model";
import { formatApiDateTime } from "@modules/shared/utils/handle-date.utils";
import { ServiceListSkeleton } from "./skeleton/ServiceListSkeleton";
import PaginationBar from "../../../components/shared/Pagination/PaginationBar";

interface ServiceListProps {
  services: GymService[];
  branches: GymBranch[];
  canManage: boolean;
  onView: (service: GymService) => void;
  onDelete: (service: GymService) => void;
  onToggleActive: (service: GymService, active: boolean) => Promise<void>;
  loading?: boolean;
}

const formatPrice = (price: number) =>
  new Intl.NumberFormat("en-US").format(price);

const unitLabel: Record<string, string> = {
  SESSION: "buổi",
  DAY: "ngày",
  MONTH: "tháng",
};

const PAGE_SIZE = 5;

export const ServiceList = ({
  services,
  branches,
  canManage,
  onView,
  onDelete,
  onToggleActive,
  loading = false,
}: ServiceListProps) => {
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [pageMap, setPageMap] = useState<Record<string, number>>({});

  // Reset pages when service list changes
  useEffect(() => {
    setPageMap({});
  }, [services]);

  // Group services by branch
  const groups = useMemo(() => {
    const byBranch = new Map<string, GymService[]>();
    services.forEach((s) => {
      const key = s.branchId || "_unknown";
      const list = byBranch.get(key) || [];
      list.push(s);
      byBranch.set(key, list);
    });

    return branches
      .map((branch) => ({
        branch,
        services: byBranch.get(branch.id) || [],
      }))
      .filter((g) => g.services.length > 0);
  }, [services, branches]);

  const handleToggle = async (service: GymService, active: boolean) => {
    setTogglingId(service.id);
    try {
      await onToggleActive(service, active);
    } finally {
      setTogglingId(null);
    }
  };

  const handlePageChange = (branchId: string, page: number) => {
    setPageMap((prev) => ({ ...prev, [branchId]: page }));
  };

  if (loading) {
    return <ServiceListSkeleton count={3} />;
  }

  if (branches.length === 0) {
    return (
      <p className="text-muted">
        Hãy thêm chi nhánh trước khi quản lý dịch vụ.
      </p>
    );
  }

  if (services.length === 0) {
    return <p className="text-muted">Chưa có dịch vụ nào.</p>;
  }

  return (
    <div className="d-flex flex-column gap-4">
      {groups.map(({ branch, services: branchServices }) => {
        const total = branchServices.length;
        const totalPages = Math.ceil(total / PAGE_SIZE);
        const currentPage = pageMap[branch.id] || 1;
        const startIndex = (currentPage - 1) * PAGE_SIZE;
        const endIndex = Math.min(startIndex + PAGE_SIZE, total);
        const paginatedServices = branchServices.slice(startIndex, endIndex);

        return (
          <div key={branch.id}>
            <div className="d-flex align-items-baseline justify-content-between mb-2">
              <div className="fw-semibold">{branch.name}</div>
              <span className="text-muted small">{total} dịch vụ</span>
            </div>
            <hr className="mt-0 mb-3" />

            <div className="d-flex flex-column gap-3">
              {paginatedServices.map((service) => (
                <div key={service.id} className="border rounded-3 p-3">
                  <div className="d-flex flex-wrap gap-3">
                    {service.images[0]?.url ? (
                      <img
                        src={service.images[0].url}
                        alt={service.name}
                        loading="lazy"
                        className="rounded"
                        style={{
                          width: 80,
                          height: 80,
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <div
                        className="rounded bg-light d-flex align-items-center justify-content-center text-muted small"
                        style={{ width: 80, height: 80, flexShrink: 0 }}
                      >
                        Không ảnh
                      </div>
                    )}
                    <div className="flex-grow-1" style={{ minWidth: 200 }}>
                      <div className="fw-semibold">{service.name}</div>
                      <div className="text-muted small">
                        {formatPrice(service.price)} VND · {service.amount}{" "}
                        {unitLabel[service.unitType] || service.unitType}
                      </div>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        <span className="badge bg-soft-secondary text-secondary">
                          {service.category}
                        </span>
                        <span
                          className={`badge ${
                            service.active
                              ? "bg-soft-success text-success"
                              : "bg-soft-secondary text-secondary"
                          }`}
                        >
                          {service.active ? "Đang bán" : "Tạm tắt"}
                        </span>
                      </div>
                      <div className="text-muted small mt-1">
                        Hết hạn: {formatApiDateTime(service.expirationAt)}
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-3 align-self-center">
                      {canManage && (
                        <div className="form-check form-switch mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id={`svc-active-${service.id}`}
                            checked={service.active}
                            disabled={togglingId === service.id}
                            onChange={(e) =>
                              handleToggle(service, e.target.checked)
                            }
                          />
                          <label
                            className="form-check-label small"
                            htmlFor={`svc-active-${service.id}`}
                          >
                            Đang bán
                          </label>
                        </div>
                      )}
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => onView(service)}
                      >
                        Xem
                      </button>
                      {canManage && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => onDelete(service)}
                        >
                          Xóa
                        </button>
                      )}
                    </div>
                  </div>
                  {service.description && (
                    <div className="text-muted small mt-2">
                      {service.description}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <PaginationBar
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => handlePageChange(branch.id, page)}
                total={total}
                pageSize={PAGE_SIZE}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
